import {
    allLifecycleEventsForPlatform,
    automationTypeMatchesLifecycleStep,
    findLifecycleAutomationRun,
    getLifecycleStepDisplayText,
} from '@/components/automations/automationHelperComponents';
import { antGrey, appHomeCardsWidgetTitleStyle, runwayColors } from '@/components/styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import { LifecycleStep, AutomationRun, ReleaseStatus, ReleaseTimelinePhase } from '@/types';
import Divider from 'antd/lib/divider';
import * as React from 'react';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '@/timeHelpers';
import { Destination } from '@/types/destinations';
import { Nullable } from '@/typescriptHelpers';
import { MiniDateBlockBase } from '@/components/appHome/widgets/TargetDatesWidget';
import { capitalize, uniq } from 'lodash';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import { CalendarOutlined, CheckCircleFilled } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';

export interface IWorkflowRunDatesWidgetProps {
    selectedDestinations: Destination[];
    workflowRunStatus: ReleaseStatus;
    workflowRunTimelinePhase: ReleaseTimelinePhase;
    workflowsEnabledLifecycleSteps: LifecycleStep[];
    targetKickoffDate?: string;
    targetSubmitDate?: string;
    targetReleaseDate?: string;
    skippedAt?: string;
    kickedOffAt?: string;
    submittedAtByDestinationStepId?: Record<string, string>;
    releasedAtByDestinationStepId?: Record<string, string>;
    legacyLifecycleAutoRuns?: AutomationRun[];
    workflowAutoRunsByDestination?: Record<string, AutomationRun[]>;
}

export const WorkflowRunDatesWidgetProps: React.FC<IWorkflowRunDatesWidgetProps> = ({
    selectedDestinations,
    workflowRunStatus,
    workflowsEnabledLifecycleSteps,
    targetKickoffDate,
    targetSubmitDate,
    targetReleaseDate,
    skippedAt,
    kickedOffAt,
    submittedAtByDestinationStepId,
    releasedAtByDestinationStepId,
    legacyLifecycleAutoRuns,
    workflowAutoRunsByDestination,
}) => {
    const timeFormat = (targetDate: dayjs.Dayjs) => (targetDate.minute() !== 0 ? 'h:mm A' : DatetimeFormat.Hour);
    const dateFormat = DatetimeFormat.ShortDayMonthDate;

    const isSkipped = workflowRunStatus === ReleaseStatus.Skipped;

    let skippedAtDate = null;
    let skippedAtTime = null;
    if (isSkipped) {
        skippedAtDate = skippedAt ? dayjs(skippedAt).format(dateFormat) : null;
        skippedAtTime = skippedAt ? dayjs(skippedAt).format(timeFormat(dayjs(skippedAt))) : null;
    }

    const lifecycleStepsData =
        isSkipped ? null : (
            getBackwardsCompatibleDateInfosForLifecycleSteps(
                selectedDestinations,
                workflowsEnabledLifecycleSteps,
                targetKickoffDate,
                targetSubmitDate,
                targetReleaseDate,
                kickedOffAt,
                submittedAtByDestinationStepId,
                releasedAtByDestinationStepId,
                legacyLifecycleAutoRuns,
                workflowAutoRunsByDestination,
            )
        );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                borderColor: antGrey.grey4,
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 8,
                backgroundColor: antGrey.grey3,
                padding: '12px 20px',
                marginBottom: 26,
            }}
        >
            {isSkipped && (
                <MiniDateBlockBase
                    displayDate={skippedAtDate}
                    displayTime={skippedAtTime}
                    title={'Skipped'}
                    automationIcon={false}
                    isComplete={false}
                    hasDate
                    shouldBeBolded={false}
                    showCadenceIcon={false}
                />
            )}
            {!isSkipped && (
                <>
                    {lifecycleStepsData.map((stepDateDatum, index) => {
                        return (
                            <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
                                {index ?
                                    <Divider style={{ width: '100%', margin: '10px 0' }} />
                                :   null}
                                <WorkflowRunMiniDateBlock
                                    selectedDestinations={selectedDestinations}
                                    {...stepDateDatum}
                                />
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
};

interface LifecycleStepDatesDatum {
    step: LifecycleStep;
    isMigratedStep: boolean;
    targetDate?: string;
    legacyActualDate?: string;
    actualDatesByDestination?: Record<string, string>;
    legacyStepAutomation?: AutomationRun;
    stepAutomationsByDestination?: Record<string, AutomationRun[]>;
}

interface WorkflowRunMiniDateBlockProps extends LifecycleStepDatesDatum {
    selectedDestinations: Destination[];
}

const WorkflowRunMiniDateBlock: React.FC<WorkflowRunMiniDateBlockProps> = ({
    step,
    selectedDestinations,
    isMigratedStep,
    targetDate,
    legacyActualDate,
    actualDatesByDestination,
}) => {
    const migratedDateInfo =
        isMigratedStep ?
            makeMigratedLifecycleDateDisplayInfo(step, selectedDestinations, targetDate, actualDatesByDestination)
        :   null;
    const legacyDateInfo =
        !isMigratedStep ? makeLegacyLifecycleDateDisplayInfo(step, targetDate, legacyActualDate) : null;

    const title = capitalize(step);

    return (
        <div style={{ minWidth: '180px' }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingBottom: '6px',
                    gap: '10px',
                }}
            >
                <Text style={{ ...appHomeCardsWidgetTitleStyle }}>{title}</Text>
            </div>
            {isMigratedStep && (
                <>
                    {!migratedDateInfo.haveAnyActualDates && migratedDateInfo.haveTargetDate && (
                        <LifecycleDateRow
                            step={step}
                            date={migratedDateInfo.targetDate}
                            haveValidDate
                            showShortDate
                            renderDestinations
                            destinations={selectedDestinations}
                            isComplete={false}
                            isTarget={true}
                        />
                    )}
                    {!migratedDateInfo.haveAnyActualDates && !migratedDateInfo.haveTargetDate && (
                        <LifecycleDateRow
                            step={step}
                            date={null}
                            haveValidDate={false}
                            showShortDate
                            renderDestinations={false}
                        />
                    )}
                    {migratedDateInfo.haveAnyActualDates && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            {migratedDateInfo.destinationLifecycleDateInfo.map((destinationDateInfo, index) => {
                                const destination = selectedDestinations.find(
                                    (dest) => dest.stepConfigId === destinationDateInfo.destinationStepId,
                                );
                                const foundMatchingDestination = !!destination;

                                return destinationDateInfo.haveActualDate ?
                                        <div key={index}>
                                            <LifecycleDateRow
                                                step={step}
                                                date={destinationDateInfo.actualStepDate}
                                                haveValidDate={destinationDateInfo.haveActualDate}
                                                showShortDate
                                                renderDestinations={foundMatchingDestination}
                                                destinations={foundMatchingDestination && [destination]}
                                                isComplete={true}
                                            />
                                        </div>
                                    :   null;
                            })}
                        </div>
                    )}
                </>
            )}
            {!isMigratedStep && (
                <LifecycleDateRow
                    step={step}
                    date={legacyDateInfo.displayDate}
                    haveValidDate={legacyDateInfo.haveActualDate || legacyDateInfo.haveTargetDate}
                    showShortDate
                    renderDestinations={legacyDateInfo.haveActualDate || legacyDateInfo.haveTargetDate}
                    destinations={
                        (legacyDateInfo.haveActualDate || legacyDateInfo.haveTargetDate) && selectedDestinations
                    }
                    isComplete={legacyDateInfo.haveActualDate}
                    isTarget={!legacyDateInfo.haveActualDate && legacyDateInfo.haveTargetDate}
                />
            )}
        </div>
    );
};

interface LifecycleDateRowProps {
    step: LifecycleStep;
    date: string;
    haveValidDate: boolean;
    renderDestinations: boolean;
    showShortDate: boolean;
    destinations?: Destination[];
    isComplete?: boolean;
    isTarget?: boolean;
}

export const LifecycleDateRow: React.FC<LifecycleDateRowProps> = ({
    step,
    date,
    haveValidDate,
    renderDestinations,
    showShortDate,
    destinations,
    isComplete,
    isTarget,
}) => {
    const maybeDate = dayjs(date);
    const maybeDateDay =
        haveValidDate &&
        maybeDate.format(showShortDate ? DatetimeFormat.ShortDayMonthDateNonOrdinal : DatetimeFormat.DayMonthDate);
    const maybeDateTime = haveValidDate && maybeDate.format(maybeDate.minute() !== 0 ? 'h:mm A' : DatetimeFormat.Hour);

    const emptyDate = showShortDate ? '--' : '-- / -- / --';
    const emptyTime = '-- : --';

    const leftDisplay = haveValidDate ? maybeDateDay : emptyDate;
    const rightDisplay = haveValidDate ? maybeDateTime : emptyTime;

    return (
        <div
            style={{
                height: '25px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                fontSize: '16px',
                gap: 10,
                color: !haveValidDate ? antGrey.grey6 : antGrey.grey8,
            }}
        >
            {renderDestinations && destinations?.length > 0 && (
                <DestinationCounter destinations={destinations} size={20} />
            )}
            <span>
                <span
                    style={{
                        fontWeight: 400,
                    }}
                >
                    {leftDisplay}
                </span>
                <span
                    style={{
                        color: !haveValidDate ? antGrey.grey5 : antGrey.grey6,
                    }}
                >
                    {'  /  '}
                </span>
                <span
                    style={{
                        fontWeight: 400,
                    }}
                >
                    {rightDisplay}
                </span>
                {!!isComplete && (
                    <span style={{ paddingLeft: '10px' }}>
                        <Tooltip title={`${capitalize(step)} completed on ${maybeDateDay} at ${maybeDateTime}.`}>
                            <CheckCircleFilled
                                style={{
                                    color: runwayColors.statusGreen,
                                }}
                            />
                        </Tooltip>
                    </span>
                )}
                {!!isTarget && (
                    <span style={{ paddingLeft: '10px' }}>
                        <Tooltip title={`Target ${step} date is ${maybeDateDay} at ${maybeDateTime}.`}>
                            <CalendarOutlined
                                style={{
                                    color: runwayColors.primary,
                                }}
                            />
                        </Tooltip>
                    </span>
                )}
            </span>
        </div>
    );
};

const getBackwardsCompatibleDateInfosForLifecycleSteps = (
    selectedDestinations: Destination[],
    workflowsEnabledLifecycleSteps: LifecycleStep[],
    targetKickoffDate?: string,
    targetSubmitDate?: string,
    targetReleaseDate?: string,
    kickedOffAt?: string,
    submittedAtByDestinationStepId?: Record<string, string>,
    releasedAtByDestinationStepId?: Record<string, string>,
    legacyLifecycleAutoRuns?: AutomationRun[],
    workflowAutoRunsByDestination?: Record<string, AutomationRun[]>,
): LifecycleStepDatesDatum[] => {
    const selectedDestinationStepIds = selectedDestinations.map((destination) => destination.stepConfigId);
    const allLifecycleStepsForDestinations: LifecycleStep[] = selectedDestinations
        .reduce((combinedLifecycleSteps: LifecycleStep[], destination: Destination) => {
            const platformLifecycleSteps = allLifecycleEventsForPlatform(destination.platform);
            return uniq([...combinedLifecycleSteps, ...platformLifecycleSteps]);
        }, [])
        .sort(lifecycleStepsSortFunc);

    return allLifecycleStepsForDestinations.map((lifecycleStep) => {
        const isWorkflowsEnabledStep = workflowsEnabledLifecycleSteps.includes(lifecycleStep);

        let targetStepDate: Nullable<string>;
        switch (lifecycleStep) {
            case LifecycleStep.Kickoff:
                targetStepDate = targetKickoffDate;
                break;
            case LifecycleStep.Submission:
                targetStepDate = targetSubmitDate;
                break;
            case LifecycleStep.Release:
                targetStepDate = targetReleaseDate;
                break;
            default:
                return null;
        }

        let legacyActualStepDate: Nullable<string>;
        let actualStepDatesByDestination: Record<string, string>;

        if (isWorkflowsEnabledStep) {
            if (lifecycleStep === LifecycleStep.Submission) {
                actualStepDatesByDestination = submittedAtByDestinationStepId;
            } else if (lifecycleStep === LifecycleStep.Release) {
                actualStepDatesByDestination = releasedAtByDestinationStepId;
            }
        } else {
            if (lifecycleStep === LifecycleStep.Kickoff) {
                legacyActualStepDate = kickedOffAt;
            }
        }

        let legacyStepAutoRun: AutomationRun;
        const stepAutoRunsByDestination: Record<string, AutomationRun[]> = {};

        if (isWorkflowsEnabledStep) {
            for (const destStepId in workflowAutoRunsByDestination) {
                if (!selectedDestinationStepIds.includes(destStepId)) {
                    continue;
                }

                const destinationAutoRuns = workflowAutoRunsByDestination[destStepId];
                if (destinationAutoRuns) {
                    stepAutoRunsByDestination[destStepId] = destinationAutoRuns.filter((autoRun) => {
                        return automationTypeMatchesLifecycleStep(lifecycleStep, autoRun.type);
                    });
                }
            }
        } else {
            legacyStepAutoRun = findLifecycleAutomationRun(lifecycleStep, legacyLifecycleAutoRuns);
        }

        const lifecycleStepDatum: LifecycleStepDatesDatum = {
            step: lifecycleStep,
            isMigratedStep: isWorkflowsEnabledStep,
            targetDate: targetStepDate,
            legacyActualDate: legacyActualStepDate,
            actualDatesByDestination: isWorkflowsEnabledStep ? actualStepDatesByDestination : null,
            legacyStepAutomation: legacyStepAutoRun,
            stepAutomationsByDestination: isWorkflowsEnabledStep ? stepAutoRunsByDestination : null,
        };

        return lifecycleStepDatum;
    });
};

const makeLegacyLifecycleDateDisplayInfo = (step: LifecycleStep, targetDate?: string, actualDate?: string) => {
    const targetStepDate = dayjs(targetDate).isValid() ? dayjs(targetDate) : null;
    const actualStepDate = dayjs(actualDate).isValid() ? dayjs(actualDate) : null;

    const haveTargetDate = !!targetStepDate && !!targetDate;
    const haveActualDate = !!actualStepDate && !!actualDate;

    const dateDisplayTitle = getLifecycleStepDisplayText(step, haveActualDate, false);

    const displayDate =
        haveActualDate ? actualDate
        : haveTargetDate ? targetDate
        : null;

    return {
        haveTargetDate,
        haveActualDate,
        dateDisplayTitle,
        displayDate,
    };
};

const makeMigratedLifecycleDateDisplayInfo = (
    step: LifecycleStep,
    selectedDestinations: Destination[],
    targetDate?: string,
    actualDatesByDestination?: Record<string, string>,
) => {
    const haveTargetDateString = !!targetDate;
    const targetDateStringIsValid = dayjs(targetDate).isValid();

    let haveAnyActualDates = false;

    const destinationLifecycleDatesResult = selectedDestinations.map((dest) => {
        const actualStepDate =
            !!actualDatesByDestination && !!actualDatesByDestination[dest.stepConfigId] ?
                actualDatesByDestination[dest.stepConfigId]
            :   null;
        const haveActualDate = !!actualStepDate;

        if (haveActualDate) {
            haveAnyActualDates = true;
        }

        return {
            destinationStepId: dest.stepConfigId,
            actualStepDate: actualStepDate,
            haveActualDate: haveActualDate,
        };
    });

    return {
        haveTargetDate: haveTargetDateString,
        haveAnyActualDates: haveAnyActualDates,
        destinationLifecycleDateInfo: destinationLifecycleDatesResult,
        targetDate: haveTargetDateString && targetDateStringIsValid ? targetDate : null,
    };
};

const lifecycleStepsSortFunc = (a: LifecycleStep, b: LifecycleStep): number => {
    const stepEnumKeys = Object.keys(LifecycleStep);

    const indexOfA = stepEnumKeys.indexOf(a);
    const indexOfB = stepEnumKeys.indexOf(b);

    return indexOfA - indexOfB;
};
